/**
 * A divider between two halves of a panel, dragged to give one more room.
 *
 * The width is kept per name, so a pane widened once stays widened the next
 * time the window is opened. Dragging is done on the pointer rather than
 * through state on every move: the pane is one element and setting its width
 * directly costs nothing, where a redraw of the editor costs a frame.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = 'inverse.splits';

function saved(): Record<string, number> {
  try {
    const held: unknown = JSON.parse(window.localStorage.getItem(KEY) ?? '{}');
    return typeof held === 'object' && held !== null ? (held as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function remember(name: string, width: number): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...saved(), [name]: Math.round(width) }));
  } catch {
    // A browser that will not keep it still lets it be dragged for now.
  }
}

export interface Split {
  /** How wide the pane is, in pixels. */
  width: number;
  /** Put on the divider itself. */
  onPointerDown: (event: React.PointerEvent) => void;
  /** Whether it is being dragged, for the cursor and the highlight. */
  dragging: boolean;
  /** Back to what it was before anybody touched it. */
  reset: () => void;
}

export function useSplitter(
  name: string,
  { initial = 272, min = 180, max = 720 }: { initial?: number; min?: number; max?: number } = {},
): Split {
  const [width, setWidth] = useState(() => {
    const held = saved()[name];
    return typeof held === 'number' && held >= min && held <= max ? held : initial;
  });
  const [dragging, setDragging] = useState(false);
  const from = useRef({ x: 0, width });

  useEffect(() => {
    if (!dragging) return;

    const moved = (event: PointerEvent): void => {
      // The pane is on the right, so dragging left makes it wider.
      const held = from.current.width + (from.current.x - event.clientX);
      setWidth(Math.min(max, Math.max(min, held)));
    };

    const done = (): void => setDragging(false);

    window.addEventListener('pointermove', moved);
    window.addEventListener('pointerup', done);
    window.addEventListener('pointercancel', done);

    return () => {
      window.removeEventListener('pointermove', moved);
      window.removeEventListener('pointerup', done);
      window.removeEventListener('pointercancel', done);
    };
  }, [dragging, min, max]);

  // Kept once the drag has finished rather than on every pixel of it.
  useEffect(() => {
    if (dragging) return;
    remember(name, width);
  }, [dragging, name, width]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      from.current = { x: event.clientX, width };
      setDragging(true);
    },
    [width],
  );

  const reset = useCallback(() => setWidth(initial), [initial]);

  return { width, onPointerDown, dragging, reset };
}
